
import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { MentionDropdown } from "@/components/comments/MentionDropdown";

// Register custom Mention format to preserve user ID
const Inline = Quill.import('blots/inline');

class MentionBlot extends Inline {
	static create(userId: string) {
		const node = super.create() as HTMLElement;
		node.setAttribute('data-mention-id', userId);
		node.classList.add('user-mention');
		return node;
	}

	static formats(node: HTMLElement) {
		const mentionId = node.getAttribute('data-mention-id');
		return mentionId ? mentionId : undefined;
	}
}

MentionBlot.blotName = 'mention';
MentionBlot.tagName = 'span';
MentionBlot.className = 'user-mention';

Quill.register('formats/mention', MentionBlot);

interface AddCommentModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (content: string) => Promise<void>;
	initialContent?: string;
	isEditing?: boolean;
}

interface User {
	id: string;
	first_name: string | null;
	last_name: string | null;
	email: string;
}

export const AddCommentModal = ({ isOpen, onClose, onSave, initialContent = "", isEditing = false }: AddCommentModalProps) => {
	const [content, setContent] = useState(initialContent);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showMentionDropdown, setShowMentionDropdown] = useState(false);
	const [mentionSearchQuery, setMentionSearchQuery] = useState("");
	const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
	const [mentionStartIndex, setMentionStartIndex] = useState(-1);
	const quillRef = useRef<ReactQuill>(null);

	// Update content when initialContent changes (for editing)
	useEffect(() => {
		if (isOpen) {
			setContent(initialContent);
			setShowMentionDropdown(false);
			setMentionSearchQuery("");
			setMentionStartIndex(-1);
		}
	}, [isOpen, initialContent]);

	// Handle Ctrl+Enter keyboard shortcut
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.ctrlKey && e.key === 'Enter' && isOpen && content.trim() && !isSubmitting && !showMentionDropdown) {
				e.preventDefault();
				handleSave();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, content, isSubmitting, showMentionDropdown]);

	const handleSave = async () => {
		if (!content.trim()) return;
		
		setIsSubmitting(true);
		try {
			await onSave(content);
			setContent("");
			onClose();
		} catch (error) {
			console.error("Error saving comment:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleContentChange = (value: string) => {
		setContent(value);

		const quill = quillRef.current?.getEditor();
		if (!quill) return;

		const selection = quill.getSelection();
		if (!selection) return;

		const cursorPosition = selection.index;
		const text = quill.getText(0, cursorPosition);
		const lastAtSymbol = text.lastIndexOf('@');

		if (lastAtSymbol !== -1 && cursorPosition > lastAtSymbol) {
			const textAfterAt = text.substring(lastAtSymbol + 1, cursorPosition);
			
			// Check if there's a space after @
			if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
				setMentionSearchQuery(textAfterAt);
				setMentionStartIndex(lastAtSymbol);
				
				// Get the position of the cursor relative to the editor
				const bounds = quill.getBounds(cursorPosition);
				const editorElement = quill.root.parentElement;
				if (editorElement) {
					const editorRect = editorElement.getBoundingClientRect();
					const dropdownHeight = 240; // max-h-60 = 240px
					const spaceBelow = editorRect.height - bounds.top - bounds.height;
					const spaceAbove = bounds.top;
					
					// Position dropdown above if not enough space below
					let top;
					if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
						// Position above the current line
						top = bounds.top - dropdownHeight - 5;
					} else {
						// Position below the current line
						top = bounds.top + bounds.height + 5;
					}
					
					setMentionPosition({
						top: top,
						left: bounds.left
					});
				}
				setShowMentionDropdown(true);
			} else {
				setShowMentionDropdown(false);
			}
		} else {
			setShowMentionDropdown(false);
		}
	};

	const handleUserSelect = (user: User) => {
		const quill = quillRef.current?.getEditor();
		if (!quill || mentionStartIndex === -1) return;

		const selection = quill.getSelection();
		if (!selection) return;

		const userName = user.first_name && user.last_name 
			? `${user.first_name} ${user.last_name}` 
			: user.first_name || user.last_name || user.email;

		// Delete the @ and search text
		quill.deleteText(mentionStartIndex, selection.index - mentionStartIndex);

		// Insert the mention text with custom format
		const mentionLength = userName.length + 1; // +1 for @
		quill.insertText(mentionStartIndex, `@${userName}`, {
			mention: user.id,
			color: '#5a7ebf',
			bold: true
		});

		// Insert a space after with no formatting
		quill.insertText(mentionStartIndex + mentionLength, ' ');

		// Set cursor position after the mention and space
		setTimeout(() => {
			quill.setSelection(mentionStartIndex + mentionLength + 1, 0);
			// Clear formatting for next text
			quill.format('mention', false);
			quill.format('color', false);
			quill.format('bold', false);
		}, 0);

		setShowMentionDropdown(false);
		setMentionSearchQuery("");
		setMentionStartIndex(-1);

		// Update content
		setContent(quill.root.innerHTML);
	};

	const quillModules = {
		toolbar: [
			['bold', 'italic', 'underline', 'strike'],
			[{ 'list': 'ordered'}, { 'list': 'bullet' }],
			['link'],
			['clean']
		],
	};

	const quillFormats = [
		'bold', 'italic', 'underline', 'strike',
		'list', 'bullet',
		'link',
		'color',
		'mention'
	];

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-[80%] w-[95%]">
				<DialogHeader>
					<DialogTitle>{isEditing ? "Edit Comment" : "Add Comment"}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-2">
						<Label htmlFor="comment">Comment (Type @ to mention a user)</Label>
						<div className="min-h-[200px] relative">
							<ReactQuill
								ref={quillRef}
								id="comment"
								placeholder="Write your comment here... Type @ to mention someone"
								value={content}
								onChange={handleContentChange}
								modules={quillModules}
								formats={quillFormats}
								className="h-[200px] mb-12"
							/>
							{showMentionDropdown && (
								<MentionDropdown
									searchQuery={mentionSearchQuery}
									onSelect={handleUserSelect}
									onClose={() => setShowMentionDropdown(false)}
									position={mentionPosition}
								/>
							)}
						</div>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button 
							onClick={handleSave} 
							disabled={!content.trim() || isSubmitting}
						>
							{isSubmitting ? "Saving..." : (isEditing ? "Update Comment" : "Save Comment")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
